/**
 * ValueChip Component
 *
 * Purpose: Reusable chip component for displaying MDM values with optional chip colors
 * Architecture: Wraps MUI Chip with value chip color logic
 * Dependencies: Material-UI Chip, theme utilities
 * Author: Development Team
 * Created: 2025-01-27
 */

import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import { getValueChipColor, ValueChipColorKey } from '../../theme/orchestrateTheme';

/**
 * ValueChip Props Interface
 */
export interface ValueChipProps extends Omit<ChipProps, 'color'> {
  /** Optional chip color key from valueChipColors (e.g., 'teal', 'blue', 'green') */
  chipColor?: ValueChipColorKey | null;
  /** The label text to display */
  label: string;
}

/**
 * ValueChip Component
 *
 * Purpose: Standardized chip display for MDM values with optional color styling
 * Logic: Applies chip color styling if chipColor is provided, otherwise uses default MUI styling
 * Input: ValueChipProps
 * Output: Styled MUI Chip component
 *
 * @example
 * // Basic usage with color
 * <ValueChip label="Active" chipColor="green" />
 *
 * @example
 * // Without color (default styling)
 * <ValueChip label="Inactive" />
 *
 * @example
 * // With additional props
 * <ValueChip label="Custom" chipColor="teal" size="small" onDelete={handleDelete} />
 */
const ValueChip: React.FC<ValueChipProps> = ({
  chipColor,
  label,
  size = 'small',
  sx,
  ...otherProps
}) => {
  // Apply chip color styling if chipColor is provided
  const chipSx = React.useMemo(() => {
    if (chipColor) {
      try {
        const colorObj = getValueChipColor(chipColor);
        return {
          backgroundColor: colorObj.main,
          color: colorObj.contrastText,
          fontWeight: 500,
          ...sx,
        };
      } catch (error) {
        // Invalid color key, use default styling
        return sx;
      }
    }
    return sx;
  }, [chipColor, sx]);

  return <Chip label={label} size={size} sx={chipSx} {...otherProps} />;
};

export default ValueChip;
